/**
 * Abrechnungslauf: orchestriert Umlage, Heizkosten und CO2 zu einem Ergebnis je
 * Mietverhältnis.
 *
 * Formelle Wirksamkeit (BGH, § 259 BGB) verlangt vier Angaben, die dieses Ergebnis
 * vollständig liefert und das PDF ausweisen muss:
 *   1. Zusammenstellung der Gesamtkosten je Kostenart
 *   2. Angabe und Erläuterung des Umlageschlüssels
 *   3. Berechnung des Anteils des Mieters
 *   4. Abzug der geleisteten Vorauszahlungen
 */

import { Cent } from './geld';
import { ISODate, Zeitraum, tage } from './zeit';
import {
  Belegungsabschnitt,
  Einheit,
  Kostenposition,
  Mietverhaeltnis,
  UmlageOptionen,
  UmlageZeile,
  belegungsabschnitte,
  verteileKostenposition,
} from './umlage';
import { HeizkostenEingabe, HeizkostenErgebnis, berechneHeizkosten } from './heizkosten';
import { Co2Aufteilung } from './co2';
import { type Vertragsstand, sollImZeitraum } from './mietverlauf';

export interface MietverhaeltnisAbrechnung extends Mietverhaeltnis {
  /** Vorauszahlungen ab Mietbeginn */
  vzNebenkostenCentProMonat: Cent;
  vzHeizkostenCentProMonat: Cent;
  betriebskostenVereinbart?: boolean;
  /** Vertragsänderungen ab Stichtag (u. a. Anpassung der Vorauszahlungen, § 560 Abs. 4 BGB). */
  staende?: Vertragsstand[];
}

export interface AbrechnungsEingabe {
  zeitraum: Zeitraum;
  einheiten: Einheit[];
  mietverhaeltnisse: MietverhaeltnisAbrechnung[];
  /** Betriebskosten ohne Heizung/Warmwasser. */
  kostenpositionen: Kostenposition[];
  /** Optional: nur wenn Heizkosten abgerechnet werden. */
  heizkosten?: Omit<HeizkostenEingabe, 'abrechnungsjahr'> & { abrechnungsjahr?: number };
  optionen?: UmlageOptionen;
}

export interface MieterErgebnis {
  mietverhaeltnisId: string;
  einheitId: string;
  tageImZeitraum: number;
  kostenNkCent: Cent;
  kostenHeizungCent: Cent;
  vorauszahlungCent: Cent;
  /** > 0 Nachzahlung, < 0 Guthaben */
  saldoCent: Cent;
  zeilen: UmlageZeile[];
}

export interface AbrechnungsErgebnis {
  zeitraum: Zeitraum;
  abrechnungsjahr: number;
  /** § 556 Abs. 3 BGB — 12 Monate nach Ende des Abrechnungszeitraums. */
  abrechnungsfristBis: ISODate;
  abschnitte: Belegungsabschnitt[];
  alleZeilen: UmlageZeile[];
  gesamtkostenJeKostenart: Record<string, Cent>;
  mieter: MieterErgebnis[];
  /** Anteil, den der Vermieter für Leerstand und Eigennutzung selbst trägt. */
  vermieterAnteilCent: Cent;
  co2: Co2Aufteilung | null;
  heizkosten: HeizkostenErgebnis | null;
  warnungen: string[];
}

export function berechneAbrechnung(eingabe: AbrechnungsEingabe): AbrechnungsErgebnis {
  const { zeitraum, einheiten, mietverhaeltnisse, kostenpositionen } = eingabe;
  const warnungen: string[] = [];
  const abrechnungsjahr = Number(zeitraum.bis.slice(0, 4));

  const abschnitte = belegungsabschnitte(
    zeitraum,
    einheiten,
    mietverhaeltnisse,
    eingabe.optionen,
  );

  // Lückenlosigkeit prüfen: die Abschnitte einer Einheit müssen den Zeitraum voll abdecken.
  const zTage = tage(zeitraum);
  for (const e of einheiten) {
    const summe = abschnitte
      .filter((a) => a.einheitId === e.id)
      .reduce((x, a) => x + a.tage, 0);
    if (summe !== zTage) {
      throw new Error(
        `Belegung der Einheit ${e.bezeichnung} deckt ${summe} von ${zTage} Tagen ab.`,
      );
    }
  }

  // Betriebskosten
  const alleZeilen: UmlageZeile[] = [];
  const gesamtkostenJeKostenart: Record<string, Cent> = {};

  for (const pos of kostenpositionen) {
    const zeilen = verteileKostenposition(pos, abschnitte, einheiten, zeitraum);
    alleZeilen.push(...zeilen);
    gesamtkostenJeKostenart[pos.kostenartCode] =
      (gesamtkostenJeKostenart[pos.kostenartCode] ?? 0) + pos.betragCent;
  }

  // Heizkosten
  let heiz: HeizkostenErgebnis | null = null;
  if (eingabe.heizkosten) {
    heiz = berechneHeizkosten(
      { ...eingabe.heizkosten, abrechnungsjahr: eingabe.heizkosten.abrechnungsjahr ?? abrechnungsjahr },
      abschnitte,
      einheiten,
      zeitraum,
    );
    alleZeilen.push(...heiz.zeilen);
    gesamtkostenJeKostenart['heizung_verbrauch'] = heiz.verbrauchsteilCent;
    gesamtkostenJeKostenart['heizung_grundkosten'] = heiz.grundkostenteilCent;
    warnungen.push(...heiz.warnungen);
  }

  const heizArten = new Set(['heizung_verbrauch', 'heizung_grundkosten']);

  // Ergebnis je Mietverhältnis
  const mieter: MieterErgebnis[] = mietverhaeltnisse
    .map((mv) => {
      const zeilen = alleZeilen.filter((z) => z.mietverhaeltnisId === mv.id);
      if (zeilen.length === 0) return null;

      const tageImZeitraum = abschnitte
        .filter((a) => a.mietverhaeltnisId === mv.id)
        .reduce((x, a) => x + a.tage, 0);

      const kostenHeizungCent = zeilen
        .filter((z) => heizArten.has(z.kostenartCode))
        .reduce((x, z) => x + z.anteilCent, 0);
      const kostenNkCent = zeilen
        .filter((z) => !heizArten.has(z.kostenartCode))
        .reduce((x, z) => x + z.anteilCent, 0);

      // Geschuldete Vorauszahlungen je Monat nach dem jeweils gültigen Vertragsstand.
      const soll = sollImZeitraum(
        {
          beginn: mv.beginn,
          ende: mv.ende,
          grundwerte: {
            kaltmieteCent: 0,
            vzNebenkostenCent: mv.vzNebenkostenCentProMonat,
            vzHeizkostenCent: mv.vzHeizkostenCentProMonat,
            personenzahl: mv.personenzahl,
          },
          staende: mv.staende,
        },
        zeitraum,
      );
      const vorauszahlungCent = soll.vzNebenkostenCent + soll.vzHeizkostenCent;

      if (mv.betriebskostenVereinbart === false) {
        warnungen.push(
          `Mietverhältnis ${mv.id}: Umlage von Betriebskosten ist im Mietvertrag nicht ` +
            'vereinbart (§ 556 Abs. 1 BGB). Eine Abrechnung ist hier nicht zulässig.',
        );
      }

      return {
        mietverhaeltnisId: mv.id,
        einheitId: mv.einheitId,
        tageImZeitraum,
        kostenNkCent,
        kostenHeizungCent,
        vorauszahlungCent,
        saldoCent: kostenNkCent + kostenHeizungCent - vorauszahlungCent,
        zeilen,
      };
    })
    .filter((x): x is MieterErgebnis => x !== null);

  const vermieterAnteilCent = alleZeilen
    .filter((z) => z.vermieterTraegt)
    .reduce((x, z) => x + z.anteilCent, 0);

  return {
    zeitraum,
    abrechnungsjahr,
    abrechnungsfristBis: plusZwoelfMonate(zeitraum.bis),
    abschnitte,
    alleZeilen,
    gesamtkostenJeKostenart,
    mieter,
    vermieterAnteilCent,
    co2: heiz?.co2 ?? null,
    heizkosten: heiz,
    warnungen,
  };
}

/** § 556 Abs. 3 S. 2 BGB: Ausschlussfrist 12 Monate nach Ende des Abrechnungszeitraums. */
export function plusZwoelfMonate(d: ISODate): ISODate {
  const [y, m, day] = d.split('-').map(Number);
  const t = new Date(Date.UTC(y + 1, m - 1, day));
  return t.toISOString().slice(0, 10);
}

/** Tage bis zum Fristablauf. Negativ = Frist verstrichen, Nachforderung ausgeschlossen. */
export function tageBisFrist(zeitraumBis: ISODate, heute: ISODate): number {
  const frist = plusZwoelfMonate(zeitraumBis);
  return (Date.parse(frist) - Date.parse(heute)) / 86_400_000;
}
